import React, { useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

const NEURONS = [
  { label: "Website Building", desc: "Conversion-grade sites generated and shipped by AI." },
  { label: "AI Chatbot", desc: "Conversational front-line that closes leads 24/7." },
  { label: "Voice AI", desc: "Inbound/outbound receptionist with human warmth." },
  { label: "Automation", desc: "Workflows that fire across CRM, email, WhatsApp." },
  { label: "Sales AI", desc: "Pipeline scoring, follow-ups, deal acceleration." },
  { label: "Marketing AI", desc: "Generates assets, ads, and segments in seconds." },
  { label: "Analytics", desc: "Predicts churn, LTV, and revenue trajectories." },
  { label: "CRM Brain", desc: "Unified memory across every customer touchpoint." },
];

function Brain({ onHover }) {
  const group = useRef();
  const nodes = useMemo(() => {
    return new Array(60).fill(0).map((_, i) => {
      const phi = Math.acos(-1 + (2 * i) / 60);
      const theta = Math.sqrt(60 * Math.PI) * phi;
      const r = 2.2;
      return new THREE.Vector3(
        r * Math.cos(theta) * Math.sin(phi),
        r * Math.sin(theta) * Math.sin(phi),
        r * Math.cos(phi)
      );
    });
  }, []);

  const edges = useMemo(() => {
    const arr = [];
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        if (nodes[i].distanceTo(nodes[j]) < 1.5 && arr.length < 130) {
          arr.push([nodes[i], nodes[j]]);
        }
      }
    }
    return arr;
  }, [nodes]);

  useFrame((s, dt) => {
    if (group.current) {
      group.current.rotation.y += dt * 0.18;
      group.current.rotation.x = Math.sin(s.clock.elapsedTime * 0.3) * 0.15;
    }
  });

  return (
    <group ref={group}>
      {nodes.map((p, i) => (
        <mesh
          key={`node-${i}`}
          position={p}
          onPointerOver={() => onHover && onHover(i)}
        >
          <sphereGeometry args={[0.045, 16, 16]} />
          <meshStandardMaterial
            color={i % 3 === 0 ? "#FF007F" : i % 3 === 1 ? "#00F0FF" : "#B200FF"}
            emissive={i % 3 === 0 ? "#FF007F" : i % 3 === 1 ? "#00F0FF" : "#B200FF"}
            emissiveIntensity={2.5}
          />
        </mesh>
      ))}
      {edges.map(([a, b], i) => {
        const geom = new THREE.BufferGeometry().setFromPoints([a, b]);
        return (
          <line key={`edge-${i}-${a.x.toFixed(2)}-${b.x.toFixed(2)}`} geometry={geom}>
            <lineBasicMaterial color="#00F0FF" transparent opacity={0.18} />
          </line>
        );
      })}
    </group>
  );
}

export default function AIBrain() {
  const [active, setActive] = useState(0);
  return (
    <section id="solutions" className="relative py-28 px-6" data-testid="ai-brain-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="font-mono text-[11px] tracking-[0.4em] text-cyan-300/80 mb-3">◆ THE AI BRAIN</p>
          <h2 className="text-4xl sm:text-5xl font-[Outfit] font-bold">
            One <span className="text-gradient">neural core.</span><br />
            Eight intelligences. Infinite outcomes.
          </h2>
          <p className="mt-4 text-white/65 max-w-xl">Every neuron is a service. They learn from each other and from your business — a single living system that thinks, sells, and operates beside your team.</p>

          <div className="mt-8 grid grid-cols-2 gap-2.5">
            {NEURONS.map((n, i) => (
              <button
                key={n.label}
                onClick={() => setActive(i)}
                data-testid={`neuron-${i}`}
                className={`text-left rounded-xl p-3 transition border ${active === i ? "border-cyan-400/60 bg-cyan-400/5 glow-cyan" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
              >
                <div className="font-[Outfit] font-semibold text-sm">{n.label}</div>
                <div className="text-[11px] text-white/55 mt-0.5 line-clamp-2">{n.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="relative h-[480px] rounded-3xl overflow-hidden glass holo-border">
          <Canvas camera={{ position: [0, 0, 6.5], fov: 50 }} dpr={[1, 1.5]}>
            <color attach="background" args={["#050816"]} />
            <ambientLight intensity={0.4} />
            <pointLight position={[3, 4, 4]} intensity={2.4} color="#B200FF" />
            <pointLight position={[-4, -2, -4]} intensity={1.8} color="#00F0FF" />
            <Brain onHover={setActive} />
          </Canvas>
          <div className="absolute bottom-4 left-4 right-4 glass-strong rounded-xl p-3 font-mono text-[11px] text-cyan-200/90" data-testid="brain-readout">
            <span className="text-white/45">NODE //</span> {NEURONS[active % NEURONS.length].label} — <span className="text-white/70">{NEURONS[active % NEURONS.length].desc}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
